import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ApiErrorService } from '../services/api-error.service';
import { ProblemDetails } from '@features/user-management/user.model';

export const problemDetailsInterceptor: HttpInterceptorFn = (request, next) => {
  const apiErrorService = inject(ApiErrorService);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        apiErrorService.setError(toProblemDetails(error));
      }

      return throwError(() => error);
    }),
  );
};

function toProblemDetails(error: HttpErrorResponse): ProblemDetails {
  const responseBody = error.error;

  if (responseBody && typeof responseBody === 'object') {
    const title = getString(responseBody, 'title', 'Title');
    if (!title) {
      return {
        title: error.statusText || 'Request failed',
        status: error.status,
        detail: error.message,
      };
    }

    return {
      title,
      status: Number(getNumber(responseBody, 'status', 'Status') ?? error.status),
      type: getString(responseBody, 'type', 'Type'),
      detail: getString(responseBody, 'detail', 'Detail') ?? error.message,
      instance: getString(responseBody, 'instance', 'Instance'),
      errors: getProblemErrors(responseBody),
    };
  }

  if (error.status === 0) {
    return {
      title: 'Network error',
      status: 0,
      detail: 'The API could not be reached. Check the backend URL and availability.',
    };
  }

  return {
    title: error.statusText || 'Request failed',
    status: error.status,
    detail: error.message,
  };
}

function getString(source: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string') {
      return value;
    }
  }

  return undefined;
}

function getNumber(source: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number') {
      return value;
    }
  }

  return undefined;
}

function getProblemErrors(source: Record<string, unknown>): Record<string, string[]> | undefined {
  const directErrors = source['errors'] ?? source['Errors'];
  const extensions = source['extensions'];
  const extensionErrors =
    extensions && typeof extensions === 'object'
      ? (extensions as Record<string, unknown>)['errors'] ?? (extensions as Record<string, unknown>)['Errors']
      : undefined;

  return normalizeProblemErrors(directErrors) ?? normalizeProblemErrors(extensionErrors);
}

function normalizeProblemErrors(value: unknown): Record<string, string[]> | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const result: Record<string, string[]> = {};

  for (const [key, entry] of Object.entries(value)) {
    const messages = toMessages(entry);
    if (messages.length > 0) {
      result[key] = messages;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function toMessages(entry: unknown): string[] {
  if (typeof entry === 'string') {
    return [entry];
  }

  if (Array.isArray(entry)) {
    const messages: string[] = [];

    for (const item of entry) {
      messages.push(...toMessages(item));
    }

    return messages;
  }

  if (!entry || typeof entry !== 'object') {
    return [];
  }

  const record = entry as Record<string, unknown>;
  const message = record['message'];
  if (typeof message === 'string' && message.length > 0) {
    return [message];
  }

  const title = record['title'];
  if (typeof title === 'string' && title.length > 0) {
    return [title];
  }

  return [];
}